import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, ModalWrapper, Skeleton, Toast } from "@/shared/ui";
import { formatMoneyKzt } from "@/shared/utils";
import { useGetProjectsByWorkerQuery } from "@/features/projects";
import { useGetRecommendedAllocationQuery } from "../../../api";
import type { ProjectAllocationItem } from "../../../types";

interface Props {
  workerId: number;
  totalAmount: number;
  fromCashDesk: boolean;
  onClose: () => void;
  onConfirm: (dto: { from_cash_desk: boolean; project_allocations: ProjectAllocationItem[] }) => void;
  isSubmitting: boolean;
}

const AMOUNT_MASK = /^\d*\.?\d{0,2}$/;

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

export default function SalaryAdvanceIssueModal({
  workerId,
  totalAmount,
  fromCashDesk,
  onClose,
  onConfirm,
  isSubmitting,
}: Props) {
  const { t } = useTranslation("AdvancesPage");
  const { data: projects, isLoading: isLoadingProjects } = useGetProjectsByWorkerQuery(workerId);
  const { data: recommended, isLoading: isLoadingRecommended } = useGetRecommendedAllocationQuery(workerId);

  const [formValues, setFormValues] = useState<Record<number, string>>({});
  const prefilledRef = useRef(false);

  useEffect(
    function prefillFromRecommendation() {
      if (prefilledRef.current) return;
      if (isLoadingProjects || isLoadingRecommended || !projects) return;
      prefilledRef.current = true;
      if (!recommended || recommended.allocations.length === 0) return;
      const projectIds = new Set(projects.map((project) => project.id));
      const usable = recommended.allocations.filter((item) => projectIds.has(item.project_id));
      if (usable.length === 0) return;
      const values: Record<number, string> = {};
      let allocated = 0;
      usable.forEach(function fill(item, index) {
        if (index === usable.length - 1) {
          const remainder = round2(totalAmount - allocated);
          values[item.project_id] = remainder > 0 ? remainder.toFixed(2) : "";
        } else {
          const amount = round2(totalAmount * parseFloat(item.ratio));
          allocated = round2(allocated + amount);
          values[item.project_id] = amount.toFixed(2);
        }
      });
      setFormValues(values);
    },
    [projects, recommended, isLoadingProjects, isLoadingRecommended, totalAmount],
  );

  function handleAmountChange(projectId: number, value: string) {
    if (value === "" || AMOUNT_MASK.test(value)) {
      setFormValues((prev) => ({ ...prev, [projectId]: value }));
    }
  }

  const totalEntered = Object.values(formValues).reduce(function sumValues(sum, value) {
    const num = parseFloat(value);
    return !isNaN(num) && num > 0 ? sum + num : sum;
  }, 0);
  const remaining = round2(totalAmount - totalEntered);
  const isOver = totalEntered - totalAmount > 0.005;
  const isExact = Math.abs(remaining) < 0.005;
  const isUnder = totalEntered > 0 && !isExact && !isOver;
  const hasProjects = !!projects && projects.length > 0;
  const confirmDisabled = isSubmitting || (hasProjects && !isExact);

  function handleConfirm() {
    const project_allocations: ProjectAllocationItem[] = (projects ?? [])
      .map(function toAllocation(project) {
        const num = parseFloat(formValues[project.id] ?? "");
        return { project_id: project.id, amount: !isNaN(num) && num > 0 ? num.toFixed(2) : "0" };
      })
      .filter(function isPositive(item) {
        return parseFloat(item.amount) > 0;
      });
    onConfirm({ from_cash_desk: fromCashDesk, project_allocations });
  }

  return (
    <ModalWrapper onClose={onClose} width="w-md">
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-2 pb-3 border-b surface-base-stroke">
          <h4 className="text-display-2xs content-base-primary">{t("issueModal.title")}</h4>
          <span className="text-body-regular-sm content-base-secondary">{formatMoneyKzt(totalAmount.toFixed(2))}</span>
        </div>

        <Toast color="grey" text={t("issueModal.payrollHint")} closable={false} autoClose={false} isFullWidth />

        {isLoadingProjects ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 3 }).map(function renderSkeleton(_, index) {
              return <Skeleton key={index} height={56} />;
            })}
          </div>
        ) : !hasProjects ? (
          <Toast color="notice" text={t("issueModal.noProjects")} closable={false} autoClose={false} isFullWidth />
        ) : (
          <div className="flex flex-col gap-3">
            {projects.map(function renderProjectRow(project) {
              return (
                <div key={project.id} className="flex flex-col gap-1">
                  <span className="text-body-regular-sm content-base-secondary">{project.name}</span>
                  <Input
                    placeholder="0"
                    value={formValues[project.id] ?? ""}
                    onChange={(event) => handleAmountChange(project.id, event.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
              );
            })}
          </div>
        )}

        {hasProjects && (
          <div className="flex items-center justify-between py-2 px-3 radius-md surface-secondary-fill">
            <span className="text-body-regular-sm content-base-secondary">{t("issueModal.remainingRow")}</span>
            <span className="text-body-bold-sm content-base-primary">{formatMoneyKzt(remaining.toFixed(2))}</span>
          </div>
        )}

        {isOver && (
          <Toast color="negative" text={t("issueModal.overAllocated")} closable={false} autoClose={false} isFullWidth />
        )}
        {isUnder && (
          <Toast
            color="notice"
            text={t("issueModal.underAllocated", { remaining: formatMoneyKzt(remaining.toFixed(2)) })}
            closable={false}
            autoClose={false}
            isFullWidth
          />
        )}

        <div className="grid grid-cols-2 gap-3">
          <Button variant="secondary" size="huge" type="button" onClick={onClose} disabled={isSubmitting}>
            {t("buttons.cancel")}
          </Button>
          <Button variant="primary" size="huge" type="button" onClick={handleConfirm} disabled={confirmDisabled}>
            {isSubmitting ? t("messages.loading") : t("issueModal.confirm")}
          </Button>
        </div>
      </div>
    </ModalWrapper>
  );
}
